import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from '@heroui/react';
import { apiFetch } from '@/lib/api';
import type { Client, LoginInfo, WorkerFieldDefinition, WorkerRow } from '@/lib/types';
import { useUserStore } from '@/store/userSlice';

export interface WorkerFormValues {
  password: string;
  internalId: string;
  clientId: string;
  workerFields: Record<string, string>;
}

const EMPTY_FORM: WorkerFormValues = {
  password: '',
  internalId: '',
  clientId: '',
  workerFields: {},
};

async function readError(res: Response): Promise<string> {
  const data = (await res.json().catch(() => null)) as { error?: string } | null;
  return data?.error ?? `Ошибка запроса (${res.status})`;
}

function formFromWorker(worker: WorkerRow): WorkerFormValues {
  return {
    password: '',
    internalId: worker.internalId ?? '',
    clientId: worker.clientId ?? '',
    workerFields: worker.workerFields ?? {},
  };
}

/** Состояние карточки «Логин» (login read-only / пароль / активность). */
export interface LoginFormValues {
  password: string;
  enabled: boolean;
}

/**
 * Анкета исполнителя: «Анкета» (телефон, внутренний ID, география) и
 * «Данные сотрудника» (настраиваемые поля клиента, вкл. ФИО — full_name).
 * Карточка «Логин» (правая колонка) — отдельный вызов бэкенда
 * GET/PATCH /admin/workers/:id/login; при создании пароль входит в анкету.
 */
export function useWorkerEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isStaff = useUserStore((s) => s.isStaff);
  const workerId = !id || id === 'new' ? null : id;

  const [worker, setWorker] = useState<WorkerRow | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [catalog, setCatalog] = useState<WorkerFieldDefinition[]>([]);
  const [ownClient, setOwnClient] = useState<Client | null>(null);
  const [form, setForm] = useState<WorkerFormValues>(EMPTY_FORM);
  const [login, setLogin] = useState<LoginInfo | null>(null);
  const [loginForm, setLoginForm] = useState<LoginFormValues>({
    password: '',
    enabled: true,
  });
  const [isLoading, setIsLoading] = useState(Boolean(workerId));
  const [notFound, setNotFound] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingLogin, setIsSavingLogin] = useState(false);

  const fetchWorker = useCallback(async () => {
    if (!workerId) return;
    setIsLoading(true);
    setNotFound(false);
    try {
      const res = await apiFetch(`/api/v1/admin/workers/${workerId}`);
      if (res.status === 404) {
        setNotFound(true);
        return;
      }
      if (!res.ok) throw new Error(await readError(res));
      const data = (await res.json()) as WorkerRow;
      setWorker(data);
      setForm(formFromWorker(data));
    } catch (e) {
      toast.danger(e instanceof Error ? e.message : 'Не удалось загрузить исполнителя');
    } finally {
      setIsLoading(false);
    }
  }, [workerId]);

  // Карточка «Логин» — отдельный вызов бэкенда
  const fetchLogin = useCallback(async () => {
    if (!workerId) return;
    try {
      const res = await apiFetch(`/api/v1/admin/workers/${workerId}/login`);
      if (!res.ok) throw new Error(await readError(res));
      const data = (await res.json()) as LoginInfo;
      setLogin(data);
      setLoginForm({ password: '', enabled: data.enabled });
    } catch (e) {
      toast.danger(e instanceof Error ? e.message : 'Не удалось загрузить логин');
    }
  }, [workerId]);

  useEffect(() => {
    void fetchWorker();
    void fetchLogin();
  }, [fetchWorker, fetchLogin]);

  useEffect(() => {
    void apiFetch('/api/v1/admin/clients/worker-field-definitions')
      .then((res) => (res.ok ? res.json() : []))
      .then((defs: WorkerFieldDefinition[]) => setCatalog(defs))
      .catch(() => setCatalog([]));
    if (isStaff) {
      void apiFetch('/api/v1/admin/clients')
        .then((res) => (res.ok ? res.json() : []))
        .then((list: Client[]) => setClients(list))
        .catch(() => setClients([]));
    } else {
      void apiFetch('/api/v1/admin/clients/me')
        .then((res) => (res.ok ? res.json() : null))
        .then((client: Client | null) => setOwnClient(client))
        .catch(() => setOwnClient(null));
    }
  }, [isStaff]);

  // Конфиг полей сотрудника: клиент — его организация, персонал — выбранный клиент.
  // Возвращаем defs со статусом ('required' — маркер обязательности в UI)
  const fieldsClient = isStaff ? (clients.find((c) => c.id === form.clientId) ?? null) : ownClient;

  const workerFieldDefs = useMemo(() => {
    const config = fieldsClient?.workerFields ?? {};
    return catalog
      .filter((def) => config[def.slug] != null)
      .map((def) => ({ ...def, status: config[def.slug]! }));
  }, [catalog, fieldsClient]);

  const setField = <K extends keyof WorkerFormValues>(key: K, value: WorkerFormValues[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const setWorkerFieldValue = (slug: string, value: string) =>
    setForm((prev) => ({
      ...prev,
      workerFields: { ...prev.workerFields, [slug]: value },
    }));

  const setLoginField = <K extends keyof LoginFormValues>(key: K, value: LoginFormValues[K]) =>
    setLoginForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    if (!workerId && !form.password) {
      toast.danger('Укажите пароль');
      return;
    }
    if (isStaff && !form.clientId) {
      toast.danger('Выберите клиента');
      return;
    }

    setIsSaving(true);
    try {
      const payload: Record<string, unknown> = {
        internal_id: form.internalId.trim() || null,
        worker_fields: form.workerFields,
      };
      if (isStaff) payload.client_id = form.clientId || null;
      // пароль — часть логина: при создании идёт с анкетой
      if (!workerId) {
        payload.password = form.password;
      }

      const res = workerId
        ? await apiFetch(`/api/v1/admin/workers/${workerId}`, {
            method: 'PATCH',
            body: JSON.stringify(payload),
          })
        : await apiFetch('/api/v1/admin/workers', {
            method: 'POST',
            body: JSON.stringify(payload),
          });
      if (!res.ok) throw new Error(await readError(res));

      toast.success(workerId ? 'Сохранено' : 'Создано');
      navigate('/workers', { replace: true });
    } catch (e) {
      toast.danger(e instanceof Error ? e.message : 'Не удалось сохранить');
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * «Обновить» в карточке «Логин»: свой PATCH, независимо от анкеты.
   * Пустой пароль = не менять. Login read-only.
   */
  const handleUpdateLogin = async () => {
    if (!workerId || isSavingLogin) return;
    setIsSavingLogin(true);
    try {
      const payload: Record<string, unknown> = { enabled: loginForm.enabled };
      if (loginForm.password) payload.password = loginForm.password;

      const res = await apiFetch(`/api/v1/admin/workers/${workerId}/login`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await readError(res));

      const data = (await res.json()) as LoginInfo;
      setLogin(data);
      setLoginForm((prev) => ({ ...prev, password: '' }));
      // статус в шапке и «Деактивирован» в «Согласиях» — свойства логина
      setWorker((prev) =>
        prev ? { ...prev, enabled: data.enabled, deactivatedAt: data.deactivatedAt } : prev,
      );
      toast.success('Логин обновлён');
    } catch (e) {
      toast.danger(e instanceof Error ? e.message : 'Не удалось обновить логин');
    } finally {
      setIsSavingLogin(false);
    }
  };

  return {
    workerId,
    worker,
    isStaff,
    clients,
    workerFieldDefs,
    form,
    login,
    loginForm,
    isLoading,
    notFound,
    isSaving,
    isSavingLogin,
    setField,
    setWorkerFieldValue,
    setLoginField,
    handleSave,
    handleUpdateLogin,
  };
}
